import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Tabs, Tab,
  CircularProgress, Alert, Divider, useTheme, useMediaQuery
} from '@mui/material';
import { format, subDays } from 'date-fns';
import { statisticsService } from '../../services/statisticsService';
import StatisticsFilters from '../../components/admin/statistics/StatisticsFilters';
import StatisticsTable from '../../components/admin/statistics/StatisticsTable';
import StatisticsCharts from '../../components/admin/statistics/StatisticsCharts';
import CountryCityStatistics from '../../components/admin/statistics/CountryCityStatistics';

// Компонент панели вкладок
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`statistics-tabpanel-${index}`}
      aria-labelledby={`statistics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: { xs: 0, sm: 0, md: 0 },
          overflowX: 'auto',
          width: '100%'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const VisitStatistics = () => {
  // Состояние
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [filters, setFilters] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    groupBy: 'day',
    isAdmin: undefined,
    excludeLocal: true, // По умолчанию исключаем локальные IP-адреса
    path: '',
    page: 0,
    limit: isMobile ? 5 : 10,
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });

  // Загрузка данных статистики
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);

        // Подготовка параметров запроса
        const params = {
          startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
          endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
          groupBy: filters.groupBy,
          isAdmin: filters.isAdmin !== undefined ? filters.isAdmin : undefined,
          path: filters.path || undefined,
          excludeLocal: filters.excludeLocal // Добавляем параметр для исключения локальных IP
        };

        // Получение общей статистики
        const statsResponse = await statisticsService.getStatistics(params);
        setStatistics(statsResponse.data);

        // Получение детальной статистики для таблицы
        const detailedParams = {
          ...params,
          page: filters.page + 1, // API использует 1-based индексацию
          limit: filters.limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder
        };
        
        const detailedResponse = await statisticsService.getDetailedStatistics(detailedParams);
        setDetailedStats(detailedResponse.data);
      } catch (err) {
        console.error('Ошибка при загрузке статистики:', err);
        setError('Не удалось загрузить данные статистики. Пожалуйста, попробуйте позже.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [
    filters.startDate, 
    filters.endDate, 
    filters.groupBy, 
    filters.isAdmin, 
    filters.path,
    filters.page,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    filters.excludeLocal
  ]);

  // Обработчик изменения вкладки
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Обработчик изменения фильтров
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Обработчик применения фильтров
  const handleApplyFilters = () => {
    // Сбрасываем страницу при применении новых фильтров
    setFilters(prev => ({ ...prev, page: 0 }));
  };

  // Обработчик сброса фильтров
  const handleResetFilters = () => {
    setFilters({
      startDate: subDays(new Date(), 30),
      endDate: new Date(),
      groupBy: 'day',
      isAdmin: undefined,
      excludeLocal: true, // Сохраняем исключение локальных IP при сбросе
      path: '',
      page: 0,
      limit: isMobile ? 5 : 10,
      sortBy: 'createdAt',
      sortOrder: 'DESC'
    });
  };

  // Обработчик изменения страницы
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Обработчик изменения количества записей на странице
  const handleLimitChange = (newLimit) => {
    setFilters(prev => ({ ...prev, limit: newLimit, page: 0 }));
  };

  return (
    <Container 
      disableGutters={true}
      maxWidth="lg" 
      sx={{ 
        py: { xs: 1.5, sm: 2.5, md: 4 },
        px: { xs: 0, sm: 0, md: 0 }
      }}
    >
      <Box sx={{ 
        mb: { xs: 1.5, sm: 2.5, md: 4 }, 
        px: { xs: 1.5, sm: 2, md: 0 }
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            mb: { xs: 1.5, sm: 2.5 }
          }}
        >
          Статистика посещений
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ 
            textAlign: { xs: 'left', sm: 'left' },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Анализ посещаемости сайта, включая информацию о странах, городах, устройствах и страницах
        </Typography>
      </Box>

      {/* Фильтры статистики */}
      <Paper sx={{ 
        mb: { xs: 1.5, sm: 2.5 },
        p: { xs: 1.5, sm: 2 },
        mx: { xs: 0, sm: 0 },
        width: '100%',
        borderRadius: { xs: 0, sm: 1 }
      }}>
        <StatisticsFilters 
          filters={filters}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
        />
      </Paper>

      {/* Отображение ошибки */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: { xs: 1.5, sm: 2.5 }, 
            mx: { xs: 1.5, sm: 2, md: 0 },
            fontSize: { xs: '0.875rem', sm: '1rem' } 
          }}
        >
          {error}
        </Alert>
      )}

      {/* Индикатор загрузки */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: { xs: 2, sm: 4 } }}>
          <CircularProgress size={isMobile ? 30 : 36} />
        </Box>
      )}

      {/* Содержимое при наличии данных */}
      {!loading && !error && (
        <>
          {/* Вкладки */}
          <Paper sx={{ 
            mb: { xs: 1.5, sm: 2.5 },
            overflowX: 'auto',
            mx: { xs: 0, sm: 0 },
            width: '100%',
            borderRadius: { xs: 0, sm: 1 }
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                  minWidth: { xs: 'auto', sm: 120 },
                  px: { xs: 1.5, sm: 2 },
                  minHeight: { xs: 48, sm: 56 }
                },
                '& .MuiTabs-scrollButtons': {
                  width: { xs: 28, sm: 40 }
                }
              }}
            >
              <Tab label="Графики" />
              <Tab label="Детальная статистика" />
            </Tabs>
          </Paper>

          {/* Панель графиков */}
          <TabPanel value={tabValue} index={0}>
            {statistics && (
              <Box sx={{ px: { xs: 0, sm: 0, md: 0 } }}>
                {/* Статистика по странам и городам */}
                <Box sx={{ mb: { xs: 2, sm: 3 }, width: '100%' }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      mb: { xs: 1, sm: 1.5 },
                      pl: { xs: 1.5, sm: 2, md: 0 }
                    }}
                  >
                    Статистика по странам и городам
                  </Typography>
                  <CountryCityStatistics 
                    countryData={statistics.visitsByCountry} 
                    cityData={statistics.visitsByCity} 
                  />
                </Box>
                
                <Divider sx={{ my: { xs: 1.5, sm: 2.5 } }} />
                
                {/* Остальные графики */}
                <StatisticsCharts statistics={statistics} />
              </Box>
            )}
          </TabPanel>

          {/* Панель детальной статистики */}
          <TabPanel value={tabValue} index={1}>
            {detailedStats && (
              <StatisticsTable 
                data={detailedStats.visits}
                page={filters.page}
                limit={filters.limit}
                totalItems={detailedStats.totalItems}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            )}
          </TabPanel>
        </>
      )}
    </Container>
  );
};

export default VisitStatistics;
